<template>
  <router-link
    class="article-card"
    :class="{
      'article-card--featured': this.featured,
      'article-card--searched': this.searched,
    }"
    :to="this.skeleton ? '#' : `/articles/${ article.slug }`"
  >
    <div
      class="article-card__picture" 
      :class="{ 'skeleton': this.skeleton }"
      :style="{ backgroundImage: this.skeleton ? null : `url(${ article.image_url })` }"
    >
      <div class="article-card__picture__gradient" v-if="!(this.skeleton)"></div>
    </div>

    <div class="article-card__preview" v-if="this.featured && this.skeleton">
      <h2 class="article-card__preview__title skeleton" style="height: 20px; width: 100%;" />
      <div class="article-card__preview__meta skeleton" style="height: 15px; width: 70%;" />
    </div>


    <div class="article-card__preview" v-else-if="this.featured">
      <h2 class="article-card__preview__title">
        {{ 
          article.title.length > 85
            ? article.title.slice(0, 85) + '...'
            : article.title
        }}
      </h2>
      <div class="article-card__preview__meta">
        {{ new Date(article.created_at) | moment("D MMMM YYYY") }} de {{ article.specialist[0].full_name }}
      </div>
    </div>

    <div class="article-card__preview" v-else-if="this.skeleton">
      <h3 class="article-card__preview__tag skeleton" style="height: 20px; width: 40%; margin-bottom: 10px;" />
      <h2 class="article-card__preview__title skeleton" style="height: 30px; width: 100%; margin-bottom: 30px;" />

      <p class="article-card__preview__summary skeleton" style="height: 15px; width: 100%;" />
      <p class="article-card__preview__summary skeleton" style="height: 15px; width: 100%;" />
      <p class="article-card__preview__summary skeleton" style="height: 15px; width: 80%;" />

      <div class="article-card__preview__meta">
        <div class="article-card__preview__avatar skeleton" />
        <span class="skeleton" style="height: 15px; width: 100%; max-width: 200px;" />
      </div>
    </div>

    <div class="article-card__preview" v-else>
      <h3 class="article-card__preview__tag">
        {{ article.tags.map(tag => tag.name).join(', ') }}
      </h3>

      <h2 class="article-card__preview__title" v-if="searched">
        {{ 
          article.title.length > 120
            ? article.title.slice(0, 120) + '...'
            : article.title
        }}
      </h2>
      <h2 class="article-card__preview__title" v-else>
        {{ 
          article.title.length > 85
            ? article.title.slice(0, 85) + '...'
            : article.title
        }}
      </h2>

      <p class="article-card__preview__summary" v-if="searched">
        {{ 
          articleDescription(article.content, 420)
        }}
      </p>
      <p class="article-card__preview__summary" v-else>
        {{ 
          articleDescription(article.content)
        }}
      </p>

      <div class="article-card__preview__meta">
        <img
          class="article-card__preview__avatar"
          :src="article.specialist[0].image_url"
        />
        {{ new Date(article.created_at) | moment("D MMMM YYYY") }} de {{ article.specialist[0].full_name }}
      </div>
    </div>
  </router-link>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

export default {
  props: [ 'article', 'featured', 'searched', 'skeleton' ],
  computed: {
    ...mapGetters({
      articles           : 'articles/articles',
      articlesLoading    : 'articles/articlesLoading',
      articlesSearch     : 'articles/articlesSearch',
      articlesTop        : 'articles/articlesTop',
      articlesTopLoading : 'articles/articlesTopLoading',
    })
  },
  methods: {
    ...mapActions({
      changeArticlesSearch : 'articles/changeArticlesSearch',
      fetchArticles        : 'articles/fetchArticles',
      fetchArticlesTop     : 'articles/fetchArticlesTop'
    }),
    articleDescription(description, stringLength = 120){
      description = description.replace(/<[^>]*>?/gm, ' ');
      return description.substring(0, stringLength) + "...";
    },
  }
}
</script>

<style lang="scss" scoped>
.article-card {
  color: inherit;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  text-decoration: none;

  @include media-breakpoint-up(md) {
    flex-direction: row;
  }

  &--featured,
  &--recent,
  &--searched {
    flex-direction: column;
  }

  &__picture {
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center;
    display: flex;
    margin-bottom: 20px;
    min-height: 265px;
    position: relative;
    width: 100%;
    transition: 200ms background-size;

    @include media-breakpoint-up(md) {
      margin-bottom: 0;
      max-width: 380px;
    }

    @include media-breakpoint-up(lg) {
      max-width: 280px;
    }

    @include media-breakpoint-up(xi) {
      max-width: 380px;
    }

    &__gradient {
      background: linear-gradient(
        135deg,
        $color-primary,
        $color-secondary
      );
      height: 100%;
      left: 0;
      mix-blend-mode: normal;
      opacity: 0;
      position: absolute;
      top: 0;
      transition: 200ms opacity;
      width: 100%;
    }
  }

  &:hover &__picture__gradient {
    opacity: 0.6;
  }

  &--featured &__picture,
  &--recent &__picture,
  &--searched &__picture {
    max-width: 100%;
  }

  &--featured &__picture {
    min-height: 110px;
  }

  &--recent &__picture {
    margin-bottom: 20px;
    min-height: 250px;
  }

  &--searched &__picture {
    margin-bottom: 20px;
    min-height: 360px;

    @include media-breakpoint-up(md) {
      margin-bottom: 40px;
    }
  }

  &__preview {
    display: flex;
    flex-direction: column;
    flex: 1;
    padding-left: 0;

    @include media-breakpoint-up(md) {
      padding-left: 20px;
    }

    &__tag {
      font-size: 16px;
      font-weight: 500;
      letter-spacing: -0.2px;
      margin-bottom: 30px;
      text-decoration: none;
    }

    &__title {
      font-size: 24px;
      font-weight: 500;
      line-height: 1.3;
      margin-bottom: 10px;
      transition: 200ms color;
    }

    &__summary {
      font-size: 16px;
      font-weight: 300;
      line-height: 1.6;
      margin-bottom: 15px;
    }

    &__meta {
      align-items: center;
      display: flex;
      font-size: 14px;
      font-weight: 300;
      letter-spacing: -0.2px;
      margin-top: auto;
    }

    &__avatar {
      background: $color-light-gray;
      border-radius: 9999px;
      height: 40px;
      margin-right: 22px;
      object-fit: cover;
      user-select: none;
      width: 40px;
    }
  }

  &--recent &__preview {
    padding-left: 0;
  }

  &--recent &__preview__title {
    margin-bottom: 20px;
  }

  &--recent &__preview__summary {
    margin-bottom: 25px;
  }

  &--searched &__preview {
    padding-right: 0;

    @include media-breakpoint-up(sm) {
     padding-right: 20px; 
    }
  }

  &--searched &__preview__title {
    margin-bottom: 20px;
  }

  &--searched &__preview__summary {
    margin-bottom: 25px;
  }

  &--searched &__preview__title,
  &:hover &__preview__title {
    color: $color-primary;
  }

  &--featured &__preview {
    padding-left: 0;
  }

  &--featured &__preview__title {
    font-size: 18px;
    font-weight: 400;
    line-height: 1.5;
    margin-top: 12px;
  }

  &--featured &__preview__meta {
    color: rgba($color-gray, 0.5);
  }
}

</style>
